import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import ListingCardV2 from "@/components/listings-v2/ListingCardV2";
import { useListingsV2 } from "@/hooks/useListingsV2";
import { useAllModulesConfig } from "@/hooks/useAuthConfig";
import { isAdminPreview } from "@/hooks/useModuleVisibility";

const FeaturedListings = () => {
  const moduleConfig = useAllModulesConfig();
  const adminPreview = isAdminPreview();
  const TYPE_CODE_TO_MODULE: Record<number, string> = {
    1: 'hospital', 2: 'doctor', 3: 'medical_store',
    6: 'diagnostic', 7: 'wholesaler',
  };
  const isModuleVisible = (typeCode: number) => {
    if (adminPreview) return true;
    const mk = TYPE_CODE_TO_MODULE[typeCode];
    if (!mk) return true;
    const status = moduleConfig?.[mk]?.visibility_status ?? 'LIVE';
    return status === 'LIVE';
  };
  const r1 = useListingsV2({ typeCode: 1, page: 1, pageSize: 4 });
  const r2 = useListingsV2({ typeCode: 2, page: 1, pageSize: 4 });
  const r3 = useListingsV2({ typeCode: 3, page: 1, pageSize: 4 });
  const r6 = useListingsV2({ typeCode: 6, page: 1, pageSize: 4 });
  const r7 = useListingsV2({ typeCode: 7, page: 1, pageSize: 4 });
  const allResults = [r1, r2, r3, r6, r7];
  const allItems = allResults.flatMap(r => r.data?.items ?? [])
    .filter(i => isModuleVisible((i as any).type_code));
  const isLoading = allResults.some(r => r.isLoading);
  const hasError = allResults.every(r => r.isError);
  const featuredItems = allItems.filter((i) => i.is_featured).slice(0, 8);
  const isFeaturedSection = featuredItems.length > 0;
  const display = isFeaturedSection ? featuredItems : allItems.slice(0, 8);

  if (hasError) return null;

  return (
    <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-white relative overflow-hidden" id="hospitals">
      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 sm:mb-12 md:mb-16 gap-4 sm:gap-6">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-cta-orange" />
              <span className="text-cta-orange font-medium">
                {isFeaturedSection ? "Featured" : "Explore"}
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3 sm:mb-4">
              {isFeaturedSection ? (
                <>Featured <span className="text-gradient">Listings</span></>
              ) : (
                <>Explore <span className="text-gradient">Providers</span></>
              )}
            </h2>
            <p className="text-muted-foreground text-lg max-w-xl">
              {isFeaturedSection
                ? "Discover top-rated healthcare providers across all categories"
                : "Browse verified healthcare providers across India"}
            </p>
          </motion.div>
          <Link to="/listings">
            <Button
              variant="outline"
              className="border-2 border-primary text-primary hover:bg-primary hover:text-white rounded-pill px-6 font-semibold transition-colors"
            >
              View All
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[4/5] animate-pulse rounded-2xl bg-muted" />
            ))}
          </div>
        ) : display.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
              <Building2 className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground font-medium mb-4">No listings available yet.</p>
            <Link to="/listings">
              <Button variant="outline" className="rounded-pill">Browse All Providers</Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
            {display.map((item) => (
              <ListingCardV2 key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedListings;
